import { useState, useEffect, useRef } from "react";
import { getUserConfig } from "../../config/user";
import { useOptionalWidgetConfig } from "../../widget/hooks/useWidgetConfig";
import { useOptionalWidgetAPI } from "../../widget/hooks/useWidgetAPI";
import { ResidentSummaryCards } from "../molecules/ResidentSummaryCards";

interface HeaderProps {
  className?: string;
  summaryData?: {
    totalCount: number;
    highRiskCount: number;
    mediumRiskCount: number;
    lowRiskCount: number;
  };
}

export function Header({ className = "", summaryData }: HeaderProps) {
  const widgetConfig = useOptionalWidgetConfig();
  const defaultUserConfig = getUserConfig();
  const apiService = useOptionalWidgetAPI();

  // Get fallback name immediately (non-blocking)
  const fallbackName = widgetConfig?.userDetails?.userName ?? defaultUserConfig.name;

  const [greeting, setGreeting] = useState<string>("");
  const [userName, setUserName] = useState<string>(fallbackName);
  const hasFetchedRef = useRef(false);

  const getTimeBasedGreeting = (): string => {
    const now = new Date();
    const hour = now.getHours();

    if (hour >= 0 && hour < 12) {
      return "Good Morning";
    } else if (hour >= 12 && hour < 18) {
      return "Good Afternoon";
    } else {
      return "Good Evening";
    }
  };

  useEffect(() => {
    // Set initial greeting
    setGreeting(getTimeBasedGreeting());

    // Update greeting every minute to handle time changes
    const interval = setInterval(() => {
      setGreeting(getTimeBasedGreeting());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Fetch current user info from API (non-blocking, best effort)
  useEffect(() => {
    // Only fetch once per component lifecycle
    if (hasFetchedRef.current || !apiService) {
      return;
    }
    hasFetchedRef.current = true;

    const fetchUserInfo = async () => {
      try {
        const userInfo = await apiService.getCurrentUser();
        // Use userName from API (e.g., "RUDOLPH SANFORD")
        if (userInfo.userName) {
          setUserName(userInfo.userName);
        }
      } catch (error) {
        // Silently fail - we already have the fallback name displayed
        if (import.meta.env.DEV) {
          console.warn("[Header] Failed to fetch user info:", error);
        }
      }
    };

    fetchUserInfo();
  }, [apiService]);

  return (
    <div className={`flex items-center justify-between gap-4 ${className} overflow-hidden`}>
      {/* LEFT: Greeting */}
      <div className="flex flex-col justify-center shrink-0">
        <div className="text-muted-foreground">
          {greeting} {userName},
        </div>
        <h1 className="text-foreground">Welcome to Your CareQueue</h1>
      </div>
      {/* RIGHT: Summary Cards */}
      <div className="flex gap-4 overflow-x-auto">
        {summaryData && (
          <ResidentSummaryCards
            totalCount={summaryData.totalCount}
            highRiskCount={summaryData.highRiskCount}
            mediumRiskCount={summaryData.mediumRiskCount}
            lowRiskCount={summaryData.lowRiskCount}
          />
        )}
      </div>
    </div>
  );
}
